import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { ShieldCheck, KeyRound, Loader2, Info } from "lucide-react";

/**
 * SecuritySettings — post-2FA-retirement view.
 * Focus is now on: password change + full-session revocation.
 * The security model itself is documented on /security (public page).
 */
const SecuritySettings = ({ token }) => {
  const [busy, setBusy] = useState(false);
  const [pw, setPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");

  const changePassword = async () => {
    if (!pw) { toast.error("Enter your current password"); return; }
    if (newPw !== confirmPw) { toast.error("New passwords don't match"); return; }
    if (newPw.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/change-password`, { current_password: pw, new_password: newPw }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.session_token) {
        localStorage.setItem("session_token", res.data.session_token);
      }
      toast.success("Password changed. All other devices signed out.");
      setPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="security-settings">
      <div>
        <h1 className="font-['Unbounded'] text-2xl font-bold text-white mb-1">Security</h1>
        <p className="text-[#8A92A0]">Manage your password. Read our full security model on the public <Link to="/security" className="text-[#EC008C] hover:underline">Security &amp; Trust</Link> page.</p>
      </div>

      {/* Security posture card — replaces the retired 2FA card */}
      <div className="card p-6" data-testid="security-posture-card">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-[#00E68A]/15 border border-[#00E68A]/30 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-6 h-6 text-[#00E68A]" />
          </div>
          <div className="flex-1">
            <h3 className="font-['Unbounded'] text-lg font-bold text-white">Your account is protected by</h3>
            <p className="text-sm text-[#8A92A0] mb-3">
              An in-house, first-party security stack — no 3rd-party sign-in, no OAuth brokers, no data leaves us.
            </p>
            <ul className="text-sm text-[#8A92A0] space-y-2 list-disc list-inside">
              <li><strong className="text-white">bcrypt password hashing</strong> — passwords never stored or logged in plain text.</li>
              <li><strong className="text-white">Brute-force lockout</strong> — repeated wrong attempts from the same IP get auto-throttled.</li>
              <li><strong className="text-white">Rotating session tokens</strong> — 7-day lifespan, silently refreshed.</li>
              <li><strong className="text-white">Global sign-out on password change</strong> — one action revokes every device.</li>
            </ul>
            <div className="mt-4 p-3 rounded-lg bg-[#1A2030]/50 border border-[#1A2030] flex items-start gap-2" data-testid="two-fa-retired-notice">
              <Info className="w-4 h-4 text-[#8A92A0] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs text-white font-semibold">Two-factor authentication is retired.</p>
                <p className="text-[11px] text-[#8A92A0] leading-relaxed mt-1">
                  The old email-OTP 2FA path has been fully removed. StardomFans.com no longer sends outbound email, and the security-stack above provides equivalent protection for a first-party consumer account.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Change password card */}
      <div className="card p-6" data-testid="change-password-card">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 rounded-xl bg-[#EC008C]/15 border border-[#EC008C]/30 flex items-center justify-center">
            <KeyRound className="w-6 h-6 text-[#EC008C]" />
          </div>
          <div>
            <h3 className="font-['Unbounded'] text-lg font-bold text-white">Change Password</h3>
            <p className="text-sm text-[#8A92A0]">Changing your password will sign out every other device instantly.</p>
          </div>
        </div>
        <div className="space-y-3">
          <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Current password"
            className="w-full bg-[#050810] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white" data-testid="current-password" />
          <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (min 8 characters)"
            className="w-full bg-[#050810] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white" data-testid="new-password" />
          <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} placeholder="Confirm new password"
            className="w-full bg-[#050810] border border-[#1A2030] rounded-lg px-3 py-2 text-sm text-white" data-testid="confirm-password" />
          <button onClick={changePassword} disabled={busy} className="px-4 py-2 rounded-lg bg-[#EC008C] text-[#000000] font-semibold hover:bg-[#FF3399] disabled:opacity-60 flex items-center gap-2 text-sm" data-testid="change-password-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Change Password
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettings;
